import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { Lock, AlertCircle } from "lucide-react";
import axios from "axios";

/**
 * LicenseGate - Conditionally renders children based on license add-on status.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component to render if license allows
 * @param {string} props.addon - The required add-on: "captainpad" | "kds"
 * @param {string} props.fallbackRoute - Where to redirect if not licensed (default: "/license")
 * @param {string} props.requiredTier - Minimum tier required (default: "base")
 */
export default function LicenseGate({ children, addon, fallbackRoute = "/license", requiredTier = "base" }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios.get("/api/license/status")
      .then(res => {
        if (mounted) {
          setStatus(res.data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (mounted) {
          setStatus({ status: "unlicensed", tier: "evaluation", addons: {} });
          setLoading(false);
        }
      });
    return () => { mounted = false; };
  }, [addon]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Checking license...</p>
        </div>
      </div>
    );
  }

  // Check if license is valid
  const isValid = status?.status === "valid";
  const tier = status?.tier || "evaluation";
  const addons = status?.addons || {};

  // Evaluation mode is pass-through, matching the server. LicenseCheckMiddleware
  // treats "no License row yet" as evaluation and lets /api/* through with an
  // X-License-Status: unlicensed header; only an invalid or EXPIRED licence is
  // refused with a 402. Blocking here on `status !== "valid"` contradicted that
  // and made a fresh install unusable — every add-on route bounced to /license
  // on a box the API was happily serving.
  const isEvaluation = status?.status === "unlicensed";

  // Tier check (base is minimum; pro would be higher but we only have base/pro)
  const tierOk = isEvaluation || (isValid && (tier === "pro" || tier === "base"));

  // Add-on check. Evaluation carries no add-on grants, so it must not be held
  // to them either — an expired or invalid licence still is.
  const addonOk = isEvaluation || (addon ? addons[addon] === true : true);

  if (!tierOk) {
    return (
      <Navigate to={fallbackRoute} replace state={{ reason: "tier", required: requiredTier }} />
    );
  }

  if (!addonOk) {
    return (
      <Navigate to={fallbackRoute} replace state={{ reason: "addon", required: addon }} />
    );
  }

  return children;
}